import { produce } from 'immer';
import cloneDeep from 'lodash/cloneDeep';

import {
  MatchingOperator,
  VariableScope,
} from 'components/automations/components/nodeTypes/types';

import { AutomationNodeTypes } from '../types/automations.types';
import { type UpdateAutomationApiArg } from '../types/automationsApi.types';

import {
  CONTACT_CUSTOM_ATTRIBUTE_PREFIX,
  CONTACT_CUSTOM_ATTRIBUTE_VARIABLETYPE,
  CONVERSATION_CUSTOM_ATTRIBUTE_PREFIX,
  CONVERSATION_CUSTOM_ATTRIBUTE_VARIABLETYPE,
} from './constants';

type MatchScopeValue = {
  value: string;
};

/**
 * Wrap the array of strings to array of objects in order to be compliant with
 * BE.
 */
const normalize = (
  matchScopeValues: string | string[][],
  operator: MatchingOperator,
): MatchScopeValue[] | undefined => {
  if (typeof matchScopeValues === 'string') {
    const temp = matchScopeValues;
    return [{ value: temp }];
  }
  if (Array.isArray(matchScopeValues)) {
    /**
     * Return empty array if the user have chosen the operator IS_EMPTY or
     * IS_NOT_EMPTY. There shouldn't be any value if the user have chosen this
     * option.
     */
    if (
      operator === MatchingOperator.IS_EMPTY ||
      operator === MatchingOperator.IS_NOT_EMPTY
    ) {
      return [{ value: '' }];
    }

    const temp = cloneDeep(matchScopeValues);
    return temp[0].map((v) => ({
      value: v,
    }));
  }
};

/**
 * Look up all condition nodes and do the following:
 *
 * 1. Iterate over all rules
 * 2. If the variable type of the rule is either a contact or conversation custom
 *    attribute, henceforth called custom attribute, then:
 * 3. Transform the custom attribute from
 *
 * { scope: 'system' variableType: 'conversationCustomAttribute', id:
 * '5772494a-302c-41c3-a4c4-fda05fd33ea2', }
 *
 * To
 *
 * { scope: 'system', variableType:
 * 'contact_custom_attribute:5772494a-302c-41c3-a4c4-fda05fd33ea2' }
 */
export const transformUpdateAutomationArg = (
  payload: UpdateAutomationApiArg['payload'],
): UpdateAutomationApiArg['payload'] => {
  return produce(payload, (draft) => {
    draft?.actions?.forEach((action) => {
      if (
        action.nodeType === AutomationNodeTypes.CONDITION ||
        action.nodeType === AutomationNodeTypes.INIT
      ) {
        action.payload.rules.forEach((rule) => {
          if (
            rule.variable.variableType === CONTACT_CUSTOM_ATTRIBUTE_VARIABLETYPE
          ) {
            rule.variable.variableType = `${CONTACT_CUSTOM_ATTRIBUTE_PREFIX}:${rule.variable.id}`;
            delete rule.variable.id;

            if (rule.match.scope === VariableScope.INPUT) {
              const temp = rule.match.values;
              rule.match.values = normalize(temp, rule.operator);
            }
          } else if (
            rule.variable.variableType ===
            CONVERSATION_CUSTOM_ATTRIBUTE_VARIABLETYPE
          ) {
            rule.variable.variableType = `${CONVERSATION_CUSTOM_ATTRIBUTE_PREFIX}:${rule.variable.id}`;
            delete rule.variable.id;

            if (rule.match.scope === VariableScope.INPUT) {
              const temp = rule.match.values;
              rule.match.values = normalize(temp, rule.operator);
            }
          }
        });
      }
    });
  });
};
